/**
 * Main application layout with navigation
 */

import React, { ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { UserRole } from '../types';
import ReportProblemButton from './ReportProblemButton';

interface LayoutProps {
  children: ReactNode;
}

export function Layout({ children }: LayoutProps) {
  const { user, logout, hasRole, hasAnyRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path: string) => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  const navItems = [
    {
      name: 'Dashboard',
      path: '/dashboard',
      show: true,
    },
    {
      name: 'Spaces',
      path: '/spaces-credentials',
      show: true,
    },
    {
      name: 'Labs',
      path: '/labs',
      show: true,
    },
    {
      name: 'My Labs',
      path: '/runs',
      show: hasAnyRole([UserRole.LEARNER, UserRole.INSTRUCTOR, UserRole.ADMIN]),
    },
    {
      name: 'SSH Keys',
      path: '/ssh-keys',
      show: true,
    },
    {
      name: 'Profile',
      path: '/profile',
      show: true,
    },
    {
      name: 'Help',
      path: '/help',
      show: true,
    },
    {
      name: 'Documentation',
      path: '/userdocs',
      show: true,
      external: true,
    },
    {
      name: 'Instructor',
      path: '/instructor',
      show: hasAnyRole([UserRole.INSTRUCTOR, UserRole.ADMIN]),
    },
    {
      name: 'Admin',
      path: '/admin',
      show: hasRole(UserRole.ADMIN),
    },
  ];

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Navigation */}
      <nav className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16">
            <div className="flex">
              {/* Logo */}
              <div className="flex-shrink-0 flex items-center">
                <Link to="/dashboard" className="text-xl font-bold text-white">
                  Do-Labs
                </Link>
              </div>

              {/* Navigation items */}
              <div className="hidden sm:ml-6 sm:flex sm:space-x-8">
                {navItems
                  .filter(item => item.show)
                  .map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className={`inline-flex items-center px-1 pt-1 border-b-2 text-sm font-medium transition-colors ${
                        isActive(item.path)
                          ? 'border-blue-500 text-white'
                          : 'border-transparent text-gray-300 hover:border-gray-300 hover:text-white'
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
              </div>
            </div>

            {/* User menu */}
            <div className="flex items-center space-x-4">
              {user && (
                <>
                  <ReportProblemButton />
                  <span className="text-sm text-gray-300">
                    {user.display_name || user.username}
                  </span>
                  <span className="px-2 py-1 text-xs bg-blue-600 text-white rounded-full capitalize">
                    {user.role}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="text-gray-300 hover:text-white text-sm font-medium transition-colors"
                  >
                    Logout
                  </button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Mobile menu */}
        <div className="sm:hidden">
          <div className="pt-2 pb-3 space-y-1">
            {navItems
              .filter(item => item.show)
              .map((item) => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`block pl-3 pr-4 py-2 border-l-4 text-base font-medium transition-colors ${
                    isActive(item.path)
                      ? 'bg-gray-900 border-blue-500 text-white'
                      : 'border-transparent text-gray-300 hover:bg-gray-700 hover:border-gray-300 hover:text-white'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
          </div>
        </div>
      </nav>

      {/* Main content */}
      <main className="flex-1">
        {children}
      </main>
    </div>
  );
}
